/**
 * The login page's Turnstile widget handle (docs/sign-in.md): one widget that switches action with the login step, so
 * a token minted for one step is never sent with the other. Split out of lib/turnstile.ts (which loads Cloudflare's
 * script and reads the site key through ./config's Vite globals) so node:test loads it as it is, with a stand-in for
 * `window.turnstile` (apps/admin/test/turnstile-handle.test.mjs). Ported from the Hype Forge Base, 2026-10-22.
 *
 * Browser only in the app; nothing here runs at import time.
 */

import type { LoginTurnstile } from './signIn';

/** The parts of Cloudflare's `window.turnstile` the handle uses. */
export interface TurnstileApi {
  render(el: HTMLElement, options: Record<string, unknown>): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

export interface TurnstileHandle extends LoginTurnstile {
  /** Wait up to `ms` for a token ('' when none came: the API then decides). */
  token(ms?: number): Promise<string>;
  /** A token is single use: get a fresh one for the next request. */
  reset(): void;
  /**
   * Run the widget for another action (a token only passes for the action it was minted for): the old widget is
   * removed and a fresh one rendered, with the same invisible-unless-needed appearance. A no-op for the current action.
   */
  use(action: string): void;
  destroy(): void;
}

/** Render the widget into `el` for `action` and hand back its handle. */
export function turnstileHandle(ts: TurnstileApi, el: HTMLElement, siteKey: string, action: string): TurnstileHandle {
  let current = '';
  let currentAction = action;
  // Each render gets its own number; a late callback from a widget that has been replaced is ignored, so a token for
  // the old action can never be sent with the new step.
  let generation = 0;
  const render = (forAction: string): string => {
    const mine = ++generation;
    const live = () => mine === generation;
    return ts.render(el, {
      sitekey: siteKey,
      action: forAction,
      appearance: 'interaction-only',
      size: 'flexible',
      callback: (token: string) => {
        if (live()) current = token;
      },
      'expired-callback': () => {
        if (live()) current = '';
      },
      'error-callback': () => {
        if (live()) current = '';
      },
    });
  };
  let id = render(action);
  return {
    async token(ms = 8000) {
      // Only a token from the widget this call started on: when the step switches the widget meanwhile (a code check
      // and "send a new code" at once), the waiting call gets '' instead of a token minted for the other action.
      const mine = generation;
      const end = Date.now() + ms;
      while (!current && Date.now() < end && mine === generation) await new Promise((r) => setTimeout(r, 100));
      return mine === generation ? current : '';
    },
    reset() {
      current = '';
      try {
        ts.reset(id);
      } catch {
        /* widget gone */
      }
    },
    use(next) {
      if (next === currentAction) return;
      current = '';
      try {
        ts.remove(id);
      } catch {
        /* already removed */
      }
      currentAction = next;
      try {
        id = render(next);
      } catch {
        /* Turnstile could not render: no token comes, and the API decides */
      }
    },
    destroy() {
      generation += 1;
      try {
        ts.remove(id);
      } catch {
        /* already removed */
      }
    },
  };
}
